/**
 * Firestore Service for Class-Tech Platform
 * خدمة إدارة قاعدة البيانات للمنصة
 */

import {
  collection,
  collectionGroup,
  doc,
  documentId,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from './firebase';
import tenantService, {
  tenantCollection,
  tenantDocument,
} from './tenantService';
import studentPaymentsService from './studentPaymentsService';
import {
  SystemSettings,
  Student,
  Parent,
  Class,
  Payment,
  Staff,
  AttendanceRecord,
  SearchFilters,
} from '../types/firestore';

// ======================== BASE FIRESTORE SERVICE ========================
class BaseFirestoreService<T> {
  protected collectionName: string;

  constructor(collectionName: string) {
    this.collectionName = collectionName;
  }

  // إنشاء مستند جديد
  async create(
    data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const now = Timestamp.now();
      const docData = {
        ...data,
        createdAt: now,
        updatedAt: now,
      } as any;

      const docRef = await addDoc(collection(db, this.collectionName), docData);
      return docRef.id;
    } catch (error) {
      console.error(`Error creating ${this.collectionName}:`, error);
      throw error;
    }
  }

  // قراءة مستند بالمعرف
  async getById(id: string): Promise<T | null> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        } as T;
      }
      return null;
    } catch (error) {
      console.error(`Error getting ${this.collectionName} by ID:`, error);
      throw error;
    }
  }

  // تحديث مستند
  async update(id: string, data: Partial<T>): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now(),
      } as any);
    } catch (error) {
      console.error(`Error updating ${this.collectionName}:`, error);
      throw error;
    }
  }

  // حذف مستند
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting ${this.collectionName}:`, error);
      throw error;
    }
  }

  // جلب جميع المستندات مع فلترة
  async getAll(filters?: SearchFilters): Promise<T[]> {
    try {
      const constraints: QueryConstraint[] = [];

      if (filters?.searchTerm) {
        // البحث النصي سيتم تنفيذه من جانب العميل
      }

      if (filters?.status) {
        constraints.push(where('status', '==', filters.status));
      }

      if (filters?.dateRange) {
        constraints.push(
          where('createdAt', '>=', filters.dateRange.start),
          where('createdAt', '<=', filters.dateRange.end)
        );
      }

      // ترتيب افتراضي
      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      let results = querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as T)
      );

      // فلترة البحث النصي من جانب العميل
      if (filters?.searchTerm) {
        const searchTerm = filters.searchTerm.toLowerCase();
        results = results.filter(item =>
          JSON.stringify(item).toLowerCase().includes(searchTerm)
        );
      }

      return results;
    } catch (error) {
      console.error(`Error getting all ${this.collectionName}:`, error);
      throw error;
    }
  }
}

// ======================== SYSTEM SETTINGS SERVICE ========================
class SystemSettingsService extends BaseFirestoreService<SystemSettings> {
  constructor() {
    super('systemSettings');
  }

  // جلب إعدادات النظام الافتراضية
  async getSystemSettings(): Promise<SystemSettings | null> {
    try {
      // نفترض أن هناك مستند واحد فقط للإعدادات
      const q = query(collection(db, this.collectionName), limit(1));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        } as SystemSettings;
      }

      // إنشاء إعدادات افتراضية إذا لم تكن موجودة
      return await this.createDefaultSettings();
    } catch (error) {
      console.error('Error getting system settings:', error);
      // إرجاع إعدادات افتراضية في حالة الخطأ
      return {
        id: 'main',
        teacherName: 'المدرس الأساسي',
        teacherEmail: 'teacher@class-tech.app',
        teacherPhone: '+966500000000',
        schoolName: 'Class-Tech',
        currency: 'EGP',
        timezone: 'Africa/Cairo',
        language: 'ar',
        allowCashPayments: true,
        allowOnlinePayments: false,
        defaultSessionPrice: 100,
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        version: '1.0.0',
      };
    }
  }

  // إنشاء إعدادات النظام الافتراضية
  private async createDefaultSettings(): Promise<SystemSettings> {
    try {
      const defaultSettings: Omit<SystemSettings, 'id'> = {
        teacherName: 'المدرس الأساسي',
        teacherEmail: 'teacher@class-tech.app',
        teacherPhone: '+966500000000',
        schoolName: 'Class-Tech',
        currency: 'EGP',
        timezone: 'Africa/Cairo',
        language: 'ar',
        allowCashPayments: true,
        allowOnlinePayments: false,
        defaultSessionPrice: 100,
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        version: '1.0.0',
      };

      const docRef = await addDoc(
        collection(db, this.collectionName),
        defaultSettings
      );

      return {
        id: docRef.id,
        ...defaultSettings,
      };
    } catch (error) {
      console.error('Error creating default settings:', error);
      // إرجاع إعدادات افتراضية محلية
      return {
        id: 'main',
        teacherName: 'المدرس الأساسي',
        teacherEmail: 'teacher@class-tech.app',
        teacherPhone: '+966500000000',
        schoolName: 'Class-Tech',
        currency: 'EGP',
        timezone: 'Africa/Cairo',
        language: 'ar',
        allowCashPayments: true,
        allowOnlinePayments: false,
        defaultSessionPrice: 100,
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        version: '1.0.0',
      };
    }
  }

  // إنشاء أو تحديث إعدادات النظام
  async updateSystemSettings(settings: Partial<SystemSettings>): Promise<void> {
    try {
      const existingSettings = await this.getSystemSettings();

      if (existingSettings) {
        await this.update(existingSettings.id, settings);
      } else {
        await this.create(settings as any);
      }
    } catch (error) {
      console.error('Error updating system settings:', error);
      throw error;
    }
  }
}

// ======================== STUDENTS SERVICE ========================
class StudentsService extends BaseFirestoreService<Student> {
  constructor() {
    super('students');
  }

  async getById(id: string): Promise<Student | null> {
    const memberships = await tenantService.getMemberships(id);
    const tenantStudents = await Promise.all(
      memberships.map(membership =>
        this.getTenantById(membership.teacherId, id)
      )
    );
    const activeTenantStudent = tenantStudents.find(
      student => student?.isActive !== false
    );
    if (activeTenantStudent) return activeTenantStudent;
    return null;
  }

  async create(
    data: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    if (!data.teacherId) {
      throw new Error('teacherId is required when creating a student');
    }
    const now = Timestamp.now();
    const docRef = await addDoc(
      collection(db, 'teachers', data.teacherId, 'students'),
      {
        ...data,
        teacherId: data.teacherId,
        tenantId: data.teacherId,
        createdAt: now,
        updatedAt: now,
      }
    );
    return docRef.id;
  }

  async getTenantById(
    teacherId: string,
    studentId: string
  ): Promise<Student | null> {
    const snapshot = await getDoc(
      doc(db, 'teachers', teacherId, 'students', studentId)
    );
    return snapshot.exists()
      ? ({ id: snapshot.id, ...snapshot.data() } as Student)
      : null;
  }

  async updateForTeacher(
    teacherId: string,
    studentId: string,
    data: Partial<Student>
  ): Promise<void> {
    const studentRef = doc(db, 'teachers', teacherId, 'students', studentId);
    const tenantSnapshot = await getDoc(studentRef);
    if (!tenantSnapshot.exists()) {
      throw new Error('Student does not belong to the current teacher');
    }
    await updateDoc(studentRef, {
      ...data,
      teacherId,
      tenantId: teacherId,
      updatedAt: Timestamp.now(),
    });
  }

  async deleteForTeacher(
    teacherId: string,
    studentId: string
  ): Promise<void> {
    const tenantRef = doc(db, 'teachers', teacherId, 'students', studentId);
    const tenantSnapshot = await getDoc(tenantRef);
    if (tenantSnapshot.exists()) {
      await deleteDoc(tenantRef);
      return;
    }

    throw new Error('Student does not belong to the current teacher');
  }

  // جلب طلاب مدرس معين — tenant-scoped: لا يُرجع طلاب مدرسين آخرين أبداً
  async getByTeacherId(teacherId: string): Promise<Student[]> {
    try {
      const q = query(
        collection(db, 'teachers', teacherId, 'students'),
        where('isActive', '==', true),
        orderBy('name')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        docSnapshot =>
          ({
            id: docSnapshot.id,
            ...docSnapshot.data(),
          } as Student)
      );
    } catch (error) {
      console.error('Error getting students by teacher:', error);
      throw error;
    }
  }

  // جلب طلاب ولي أمر معين
  async getByParentId(parentId: string): Promise<Student[]> {
    try {
      const memberships = await tenantService.getMemberships(parentId);
      const tenantSnapshots = await Promise.all(
        memberships.map(membership =>
          getDocs(
            query(
              tenantCollection(membership.teacherId, 'students'),
              where('parentId', '==', parentId),
              where('isActive', '==', true),
              orderBy('name')
            )
          )
        )
      );

      const tenantStudents = tenantSnapshots.flatMap((snapshot, index) =>
        snapshot.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          return {
            id: docSnapshot.id,
            ...data,
            // Legacy tenant records may not contain teacherId. Preserve the
            // tenant context so parent screens never need to read the child's
            // private membership subcollection to rediscover it.
            teacherId: data.teacherId || memberships[index].teacherId,
          } as Student;
        })
      );

      return tenantStudents;
    } catch (error) {
      console.error('Error getting students by parent:', error);
      throw error;
    }
  }

  // ربط طالب بولي أمر
  async linkToParent(studentId: string, parentId: string): Promise<void> {
    try {
      await this.update(studentId, { parentId });
    } catch (error) {
      console.error('Error linking student to parent:', error);
      throw error;
    }
  }
}

// ======================== PARENTS SERVICE ========================
class ParentsService extends BaseFirestoreService<Parent> {
  constructor() {
    super('parents');
  }

  // جلب ولي أمر بالإيميل
  async getByEmail(email: string): Promise<Parent | null> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('email', '==', email),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        } as Parent;
      }
      return null;
    } catch (error) {
      console.error('Error getting parent by email:', error);
      throw error;
    }
  }

  // إضافة طالب إلى قائمة الأطفال
  async addChild(parentId: string, studentId: string): Promise<void> {
    try {
      const parent = await this.getById(parentId);
      if (parent) {
        const updatedChildren = [...parent.children, studentId];
        await this.update(parentId, { children: updatedChildren });
      }
    } catch (error) {
      console.error('Error adding child to parent:', error);
      throw error;
    }
  }
}

// ======================== CLASSES SERVICE ========================
class ClassesService extends BaseFirestoreService<Class> {
  constructor() {
    super('classes');
  }

  async create(
    data: Omit<Class, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    if (!data.teacherId) {
      throw new Error('teacherId is required when creating a class');
    }
    const now = Timestamp.now();
    const classData = data as Partial<Class> & {
      teacherId: string;
      tenantId?: string;
    };
    const docRef = await addDoc(
      collection(db, 'teachers', data.teacherId, 'classes'),
      {
        name: classData.name?.trim(),
        subject: classData.subject?.trim(),
        description: classData.description?.trim() || '',
        teacherId: classData.teacherId,
        tenantId: classData.teacherId,
        enrolledStudents: classData.enrolledStudents || [],
        maxStudents: classData.maxStudents || 30,
        schedule: classData.schedule || null,
        pricePerSession: classData.pricePerSession || 0,
        currency: classData.currency || 'EGP',
        level: classData.level || 'intermediate',
        isActive: classData.isActive !== false,
        status: 'active',
        isPublic: classData.isPublic === true,
        totalSessions: classData.totalSessions || 0,
        completedSessions: classData.completedSessions || 0,
        createdAt: now,
        updatedAt: now,
      }
    );
    return docRef.id;
  }

  async getTenantById(
    teacherId: string,
    classId: string
  ): Promise<Class | null> {
    const snapshot = await getDoc(
      doc(db, 'teachers', teacherId, 'classes', classId)
    );
    return snapshot.exists()
      ? ({ id: snapshot.id, ...snapshot.data(), currency: 'EGP' } as Class)
      : null;
  }

  async getByStudentId(
    studentId: string,
    teacherIds?: string[]
  ): Promise<Class[]> {
    try {
      const resolvedTeacherIds =
        teacherIds && teacherIds.length > 0
          ? teacherIds
          : (await tenantService.getMemberships(studentId)).map(
              membership => membership.teacherId
            );
      const tenantSnapshots = await Promise.all(
        resolvedTeacherIds.map(teacherId =>
          getDocs(
            query(
              tenantCollection(teacherId, 'classes'),
              where('enrolledStudents', 'array-contains', studentId),
              where('isActive', '==', true)
            )
          )
        )
      );
      const tenantClasses = tenantSnapshots.flatMap(snapshot =>
        snapshot.docs.map(
          docSnapshot =>
            ({
              id: docSnapshot.id,
              ...docSnapshot.data(),
              currency: 'EGP',
            } as Class)
        )
      );

      return tenantClasses;
    } catch (error) {
      console.error('Error getting classes by student:', error);
      throw error;
    }
  }

  async updateForTeacher(
    teacherId: string,
    classId: string,
    data: Partial<Class>
  ): Promise<void> {
    const classRef = doc(db, 'teachers', teacherId, 'classes', classId);
    const tenantSnapshot = await getDoc(classRef);
    if (!tenantSnapshot.exists()) {
      throw new Error('Class does not belong to the current teacher');
    }
    const updates = { ...(data as Partial<Class> & { tenantId?: string }) };
    delete (updates as any).id;
    delete (updates as any).createdAt;
    delete (updates as any).teacherId;
    delete (updates as any).tenantId;
    await updateDoc(classRef, {
      ...updates,
      teacherId,
      tenantId: teacherId,
      updatedAt: Timestamp.now(),
    });
  }

  async deleteForTeacher(teacherId: string, classId: string): Promise<void> {
    const tenantRef = doc(db, 'teachers', teacherId, 'classes', classId);
    const tenantSnapshot = await getDoc(tenantRef);
    if (tenantSnapshot.exists()) {
      const now = Timestamp.now();
      await updateDoc(tenantRef, {
        isActive: false,
        status: 'archived',
        archivedAt: now,
        updatedAt: now,
      });
      return;
    }

    throw new Error('Class does not belong to the current teacher');
  }

  // جلب صفوف مدرس معين
  async getByTeacherId(teacherId: string): Promise<Class[]> {
    try {
      const q = query(
        collection(db, 'teachers', teacherId, 'classes'),
        where('teacherId', '==', teacherId),
        where('isActive', '==', true),
        orderBy('name')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        docSnapshot =>
          ({
            id: docSnapshot.id,
            ...docSnapshot.data(),
          } as Class)
      );
    } catch (error) {
      console.error('Error getting classes by teacher:', error);
      throw error;
    }
  }

  // إضافة طالب إلى صف
  async enrollStudent(classId: string, studentId: string): Promise<void> {
    try {
      const classDoc = await this.getById(classId);
      if (classDoc) {
        const updatedStudents = [...classDoc.enrolledStudents, studentId];
        await this.update(classId, { enrolledStudents: updatedStudents });
      }
    } catch (error) {
      console.error('Error enrolling student in class:', error);
      throw error;
    }
  }

  // إزالة طالب من صف
  async unenrollStudent(classId: string, studentId: string): Promise<void> {
    try {
      const classDoc = await this.getById(classId);
      if (classDoc) {
        const updatedStudents = classDoc.enrolledStudents.filter(
          id => id !== studentId
        );
        await this.update(classId, { enrolledStudents: updatedStudents });
      }
    } catch (error) {
      console.error('Error unenrolling student from class:', error);
      throw error;
    }
  }
}

// ======================== PAYMENTS SERVICE ========================
class PaymentsService extends BaseFirestoreService<Payment> {
  constructor() {
    super('payments');
  }

  async create(
    data: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    return studentPaymentsService.createStudentInvoice(data.teacherId, {
      studentId: data.studentId,
      amount: data.amount,
      paymentMethod: data.paymentMethod,
      description: data.description,
      dueDate: data.dueDate,
      notes: data.notes,
      referenceNumber: data.receiptNumber,
      receipt: Boolean(data.receiptUrl || data.receiptNumber),
    });
  }

  async getTenantById(
    teacherId: string,
    paymentId: string
  ): Promise<Payment | null> {
    const snapshot = await getDoc(
      doc(db, 'teachers', teacherId, 'payments', paymentId)
    );
    return snapshot.exists()
      ? ({ id: snapshot.id, ...snapshot.data(), currency: 'EGP' } as Payment)
      : null;
  }

  // جلب مدفوعات طالب معين
  async getByStudentId(
    studentId: string,
    teacherIds?: string[]
  ): Promise<Payment[]> {
    try {
      return studentPaymentsService.listStudentPayments(
        studentId,
        teacherIds
      ) as Promise<Payment[]>;
    } catch (error) {
      console.error('Error getting payments by student:', error);
      throw error;
    }
  }

  // جلب مدفوعات مدرس معين
  async getByTeacherId(teacherId: string): Promise<Payment[]> {
    try {
      return studentPaymentsService.listTeacherPayments(
        teacherId
      ) as Promise<Payment[]>;
    } catch (error) {
      console.error('Error getting payments by teacher:', error);
      throw error;
    }
  }

  // جلب المدفوعات المتأخرة
  async getOverduePayments(teacherId: string): Promise<Payment[]> {
    try {
      const payments = await studentPaymentsService.listTeacherPayments(
        teacherId
      );
      return payments.filter(payment => payment.status === 'overdue') as Payment[];
    } catch (error) {
      console.error('Error getting overdue payments:', error);
      throw error;
    }
  }

  // تحديث حالة الدفع
  async updatePaymentStatus(
    teacherId: string,
    paymentId: string,
    status: Payment['status'],
    paidAt?: Timestamp
  ): Promise<void> {
    try {
      await studentPaymentsService.updateStudentPaymentStatus(
        teacherId,
        paymentId,
        status
      );
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  }
}

// ======================== STAFF SERVICE ========================
class StaffService extends BaseFirestoreService<Staff> {
  constructor() {
    super('staff');
  }

  // جلب الموظفين النشطين
  async getActiveStaff(): Promise<Staff[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('isActive', '==', true),
        orderBy('name')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Staff)
      );
    } catch (error) {
      console.error('Error getting active staff:', error);
      throw error;
    }
  }

  // جلب موظف بالإيميل
  async getByEmail(email: string): Promise<Staff | null> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('email', '==', email),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        } as Staff;
      }
      return null;
    } catch (error) {
      console.error('Error getting staff by email:', error);
      throw error;
    }
  }
}

// ======================== ATTENDANCE SERVICE ========================
class AttendanceService extends BaseFirestoreService<AttendanceRecord> {
  constructor() {
    super('attendance');
  }

  // تسجيل حضور
  async markAttendance(
    personId: string,
    personType: 'student' | 'staff',
    status: AttendanceRecord['status'],
    classId?: string,
    sessionId?: string
  ): Promise<void> {
    try {
      if (!sessionId) {
        throw new Error('sessionId is required for tenant attendance');
      }
      const sessionRefs = await this.resolveSessionRefs(sessionId);
      const sessionSnapshot = sessionRefs.tenant
        ? await getDoc(sessionRefs.tenant)
        : null;
      const sessionData = sessionSnapshot?.exists()
        ? sessionSnapshot.data()
        : null;
      const teacherId = sessionData?.teacherId;
      if (!teacherId) {
        throw new Error('Teacher ownership is required for attendance');
      }
      const attendanceData: Omit<
        AttendanceRecord,
        'id' | 'createdAt' | 'updatedAt'
      > = {
        personId,
        personType,
        date: Timestamp.now(),
        status,
        classId,
        sessionId,
        recordedAt: Timestamp.now(),
      };

      await addDoc(tenantCollection(teacherId, 'attendance'), {
        ...attendanceData,
        teacherId,
        tenantId: teacherId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error marking attendance:', error);
      throw error;
    }
  }

  private async resolveSessionRefs(sessionId: string) {
    const sessionSnapshot = await getDocs(
      query(
        collectionGroup(db, 'sessions'),
        where(documentId(), '==', sessionId),
        limit(1)
      )
    );
    return {
      tenant: sessionSnapshot.empty
        ? null
        : (sessionSnapshot.docs[0].ref as ReturnType<typeof tenantDocument>),
    };
  }

  // جلب سجل حضور شخص معين
  async getByPersonId(
    personId: string,
    startDate?: Timestamp,
    endDate?: Timestamp
  ): Promise<AttendanceRecord[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('personId', '==', personId),
        orderBy('date', 'desc'),
      ];

      if (startDate) {
        constraints.push(where('date', '>=', startDate));
      }
      if (endDate) {
        constraints.push(where('date', '<=', endDate));
      }

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as AttendanceRecord)
      );
    } catch (error) {
      console.error('Error getting attendance by person:', error);
      throw error;
    }
  }

  // جلب حضور صف معين
  async getByClassId(
    classId: string,
    date?: Timestamp
  ): Promise<AttendanceRecord[]> {
    try {
      const constraints: QueryConstraint[] = [where('classId', '==', classId)];

      if (date) {
        // حضور يوم معين
        const startOfDay = new Date(date.toDate());
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(date.toDate());
        endOfDay.setHours(23, 59, 59, 999);

        constraints.push(
          where('date', '>=', Timestamp.fromDate(startOfDay)),
          where('date', '<=', Timestamp.fromDate(endOfDay))
        );
      }

      constraints.push(orderBy('date', 'desc'));

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(
        doc =>
          ({
            id: doc.id,
            ...doc.data(),
          } as AttendanceRecord)
      );
    } catch (error) {
      console.error('Error getting attendance by class:', error);
      throw error;
    }
  }
}

// ======================== EXPORT SERVICES ========================
export const systemSettingsService = new SystemSettingsService();
export const studentsService = new StudentsService();
export const parentsService = new ParentsService();
export const classesService = new ClassesService();
export const paymentsService = new PaymentsService();
export const staffService = new StaffService();
export const attendanceService = new AttendanceService();

// خدمات مجمعة لسهولة الاستيراد
export const firestoreServices = {
  systemSettings: systemSettingsService,
  students: studentsService,
  parents: parentsService,
  classes: classesService,
  payments: paymentsService,
  staff: staffService,
  attendance: attendanceService,
};

export default firestoreServices;
